<template>
  <div v-resize="onResize" class="px-4">
    <a-data-table
      v-model="selectedFiles"
      :headers="headers"
      :items="documentItems"
      :search="search"
      dense
      fixed-header
      :height="screenHeight"
      :items-per-page="-1"
      hide-default-footer
      mobile-breakpoint="0"
      :no-data-text="$t('noData')"
      :no-results-text="$t('emptySearchRecords')"
      show-select
      item-key="documentId"
      :loading="isDataLoading"
      :loading-text="loadtext"
      :sort-by="sortOnColumn"
      :sort-desc="sortType"
      :must-sort="true"
    >
      <template v-slot:[`item.fileName`]="{ item }">
        <v-icon class="mr-2" v-if="!item.extension"> mdi-file </v-icon>
        <v-icon class="mr-2" v-else>
          {{ fileIcons[item.extension] || "mdi-file" }}
        </v-icon>
        <a
          class="title"
          v-if="
            $security.checkMeetingTypeAccess(
              item.meetingTypeId,
              'OpenMeetingDocument'
            )
          "
          @click="onFileDownload(item)"
          >{{ item.fileName }}</a
        >
        <span v-else>{{ item.fileName }}</span>
      </template>

      <template v-slot:[`item.topicText`]="{ item }">
        <p class="subtitle-2 pa-0 ma-0 overflow-wrap">
          {{ item.topicText }}
        </p>
      </template>

      <template v-slot:[`item.datetime`]="{ item }">
        <p class="subtitle-2 pa-0 ma-0">
          {{ getDate(item.datetime) }}
        </p>
      </template>
    </a-data-table>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { openDocument, isoIntlDateFormat } from "../../utils/util";
import _ from "lodash";

export default {
  name: "MeetingDocumentsListView",
  components: {},
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    search: String,
    value: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    defaultSortOrder: {
      type: Object,
      default: () => ({ sortOnColumn: "datetime", sortType: "desc" }),
    },
  },
  data() {
    return {
      fileIcons: {
        zip: "mdi-folder-zip-outline",
        rar: "mdi-folder-zip-outline",
        htm: "mdi-language-html5",
        html: "mdi-language-html5",
        js: "mdi-nodejs",
        json: "mdi-json",
        md: "mdi-markdown",
        pdf: "mdi-file-pdf-box",
        png: "mdi-file-image",
        jpg: "mdi-file-image",
        jpeg: "mdi-file-image",
        mp4: "mdi-filmstrip",
        mkv: "mdi-filmstrip",
        avi: "mdi-filmstrip",
        wmv: "mdi-filmstrip",
        mov: "mdi-filmstrip",
        txt: "mdi-file-document-outline",
        xls: "mdi-file-excel",
        other: "mdi-file-outline",
      },
      screenHeight: 0,
    };
  },
  computed: {
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    sortOnColumn() {
      if (this.defaultSortOrder) {
        return this.defaultSortOrder.sortOnColumn;
      }
      return "datetime";
    },
    sortType() {
      if (this.defaultSortOrder) {
        return this.defaultSortOrder.sortType === "desc";
      }
      return true;
    },
    isDataLoading() {
      return this.loading;
    },
    selectedFiles: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || []);
      },
    },
    documentItems() {
      const documentList = [];
      this.files.forEach((document) => {
        documentList.push({
          topicText: this.getTopicName(document) || "-",
          fileName: document.fileName,
          size: "100kb",
          lastModified: document.lastModified || "2022-01-04 09:10",
          extension: document.fileName.split(".").pop(),
          documentId: document.id,
          meetingTypeId: document.meetingTypeId,
          datetime: document.uploaded_at || document._ts,
        });
      });
      // this.files.topics.forEach((topic) => {
      //   topic.documents.forEach((document) => {
      //     documentList.push({
      //       topicText: topic.topicText,
      //       fileName: document.fileName,
      //       size: "100kb",
      //       lastModified: document.lastModified || "2022-01-04 09:10",
      //       extension: document.extension,
      //       documentId: document.documentId,
      //     });
      //   });
      // });
      return documentList;
    },
    headers() {
      return [
        {
          text: this.$t("lblSearchTextDocumentFolder"),
          align: "left",
          value: "fileName",
          class: "header header-text",
        },
        {
          text: this.$t("documents.hBelongTo"),
          align: "left",
          value: "topicText",
          class: "header header-text",
        },
        {
          text: this.$t("documents.lblUploadTime"),
          align: "left",
          value: "datetime",
          class: "header header-text",
        },
        // {
        //   text: this.$t("documents.modified"),
        //   align: "left",
        //   value: "lastModified",
        // },
        // {
        //   text: this.$t("documents.size"),
        //   align: "left",
        //   value: "size",
        // },
      ];
    },
    loadtext() {
      this.isloading = true;
      return this.documentItems.length > 0
        ? this.$t("LoaderText")
        : this.$t("noData");
    },
  },
  methods: {
    ...mapActions({
      downloadDocumentAPI: "document/downloadDocument",
    }),
    getTopicName(document) {
      const { momDocuments = [], topics = [] } = document.meetingObj;
      const { id, document_type, topicid } = document;

      // Check if the document is an MoM document
      const isMOMDoc = momDocuments.some((t) => t.documentId === id);
      if (isMOMDoc) {
        return this.$t("agenda.lblMOM");
      }
      // Check if the document is a Transcript document
      if (
        document_type === "transcription_result" ||
        document_type === "transcription_input"
      ) {
        return this.$t("documents.lblTranscriptionDoc");
      }
      // Check if the document is a general document "description_image"
      if (document_type === "general_document") {
        return this.$t("documents.lblGenDoc");
      }
      // Check if the description image
      if (document_type === "description_image") {
        return this.$t("documents.lblDescriptionImg");
      }
      // Return "-" if there is no associated topic ID
      if (!topicid) {
        return "-";
      }
      // Find and return the topic name if a matching topic is found
      const topic = topics.find((t) => t.Id === topicid);
      return topic ? `${this.$t("documents.topic")}: ${topic.topicName}` : "-";
    },
    customSorting(items, sortBy, sortDesc) {
      if (sortBy[0] === "datetime" && Boolean(sortDesc)) {
        return _.orderBy(items, [sortBy[0]], [sortDesc[0] ? "desc" : "asc"]);
      }
      if (sortBy[0] === "fileName" || sortBy[0] === "topicText") {
        return _.orderBy(
          items,
          [
            (item) => {
              const tokens = item[sortBy[0]].split(/(\d+)/).filter(Boolean);
              return tokens.map((token) => {
                if (!isNaN(token)) {
                  return parseInt(token, 10);
                }
                return token.toLowerCase();
              });
            },
          ],
          [sortDesc[0] ? "desc" : "asc", "asc"]
        );
      }
      return items;
    },
    getDate(date) {
      return isoIntlDateFormat(this.$i18n.locale, date);
    },
    onResize() {
      this.screenHeight = window.innerHeight - 275;
    },
    async onFileDownload(file) {
      const params = {
        documentId: file.documentId,
        filename: file.fileName,
      };
      const res = await this.downloadDocumentAPI(params);
      let contentType = res.headers["content-type"];
      let blob = new Blob([res.data], { type: contentType });
      let url = window.URL.createObjectURL(blob);
      openDocument(url, file.fileName);
    },
  },
};
</script>

<style scoped>
.v-application .title {
  font-size: 16px !important;
  line-height: 24px !important;
  font-family: "Noto Sans JP" !important;
}
.v-application .subtitle-2 {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: var(--v-darkCharcoal-base) !important;
  line-height: 21px !important;
  font-family: "Noto Sans JP" !important;
}
::v-deep thead th {
  padding: 4px 12px !important;
}
::v-deep tbody td {
  border-bottom: thin solid var(--v-secondary-base) !important;
  padding: 5px 12px !important;
  height: 20px !important;
}
::v-deep tbody tr {
  height: 20px !important;
}
::v-deep th {
  background: var(--v-neutralGray-base) !important;
  color: #ffffff !important;
  font-family: "Noto Sans JP" !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 18px !important;
}
::v-deep .v-input--selection-controls__ripple {
  border-radius: unset !important;
  height: unset !important;
  width: unset !important;
}

::v-deep .v-simple-checkbox .v-icon {
  cursor: pointer;
}

::v-deep
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td {
  height: 20px !important;
}
</style>